import { isNil } from 'es-toolkit';

import { CLIENT_PORTAL_ALLOCATION_COLORS, CLIENT_PORTAL_UNCATEGORISED_ASSET_CLASS_LABEL } from './constants';
import { safePercent, toFiniteNumber } from './utils';

export type ClientPortalAllocationSlice = {
  readonly name: string;
  readonly percent: number;
  readonly value: number;
  readonly color: string;
};

function colorAt(index: number): string {
  return CLIENT_PORTAL_ALLOCATION_COLORS[index % CLIENT_PORTAL_ALLOCATION_COLORS.length] ?? 'var(--chart-1)';
}

export type ClientPortalAllocationEntry = {
  readonly name: string;
  readonly value: number;
  readonly category?: string | null;
};

// Orion's per-holding assetCategory values, ranked so the ratio sort clusters equities first, then
// fixed income, alternatives, and cash — the order advisors discuss allocation in (60/40 stocks/bonds).
// Values confirmed against live Orion data; anything unrecognized or missing sorts last. Match the
// strings exactly, including the ampersand in 'Cash & Cash Equivalents'. Plain 'Cash' is an alias
// Orion returns for sandbox/held-away cash — map it to the same bucket so it doesn't fall to the tail.
export const CLIENT_PORTAL_ASSET_CATEGORY_RANK: Readonly<Record<string, number>> = {
  Equity: 0,
  'Fixed Income': 1,
  Alternatives: 2,
  'Cash & Cash Equivalents': 3,
  Cash: 3,
};
export const CLIENT_PORTAL_ASSET_CATEGORY_RANK_FALLBACK = 4;

function categoryRank(category: string | null | undefined): number {
  if (isNil(category)) {
    return CLIENT_PORTAL_ASSET_CATEGORY_RANK_FALLBACK;
  }
  return CLIENT_PORTAL_ASSET_CATEGORY_RANK[category] ?? CLIENT_PORTAL_ASSET_CATEGORY_RANK_FALLBACK;
}

export function clientPortalAllocationSlicesFromEntries(
  entries: readonly ClientPortalAllocationEntry[],
  sortByRatio = false,
): readonly ClientPortalAllocationSlice[] {
  const filtered = entries.filter((entry) => entry.value > 0);
  const held = sortByRatio
    ? filtered.sort(
        (left, right) => categoryRank(left.category) - categoryRank(right.category) || right.value - left.value,
      )
    : filtered.sort((left, right) => right.value - left.value);

  const total = held.reduce((sum, slice) => sum + slice.value, 0);
  if (total === 0) {
    return [];
  }

  return held.map((slice, index) => ({
    name: slice.name,
    value: slice.value,
    percent: (slice.value / total) * 100,
    color: colorAt(index),
  }));
}

export type ClientPortalClassedAmount = {
  readonly name: string | null | undefined;
  readonly amount: number | null;
  readonly category?: string | null;
};

export function clientPortalAllocationSlicesByAssetClass(
  classed: readonly ClientPortalClassedAmount[],
  sortByRatio = false,
): readonly ClientPortalAllocationSlice[] {
  const byClass = new Map<string, number>();
  // A class's category for the ratio sort is the first non-null category among its holdings. Every
  // holding in one asset class shares one Orion category, but a holding can arrive with a null
  // category (e.g. MoneyKit stored holdings) — that must not lock the class to null when a later
  // holding in the same class carries a real category. Classes never seen with a category stay
  // absent here and fall back to the unranked bucket downstream.
  const categoryByClass = new Map<string, string>();
  for (const entry of classed) {
    if (isNil(entry.amount)) {
      continue;
    }
    const name = isNil(entry.name) || entry.name === '' ? CLIENT_PORTAL_UNCATEGORISED_ASSET_CLASS_LABEL : entry.name;
    byClass.set(name, (byClass.get(name) ?? 0) + entry.amount);
    if (!isNil(entry.category) && !categoryByClass.has(name)) {
      categoryByClass.set(name, entry.category);
    }
  }
  return clientPortalAllocationSlicesFromEntries(
    [...byClass].map(([name, value]) => ({ name, value, category: categoryByClass.get(name) ?? null })),
    sortByRatio,
  );
}

export type ClientPortalReportHolding = {
  readonly assetClass: string | null;
  readonly assetCategory?: string | null;
  readonly value: { readonly amount: string | number | null } | null;
};

export function clientPortalAllocationSlicesFromReportHoldings(
  holdings: readonly ClientPortalReportHolding[] | undefined | null,
  sortByRatio = false,
): readonly ClientPortalAllocationSlice[] {
  if (isNil(holdings)) {
    return [];
  }
  return clientPortalAllocationSlicesByAssetClass(
    holdings.map((holding) => ({
      name: holding.assetClass,
      amount: toFiniteNumber(holding.value?.amount),
      category: holding.assetCategory,
    })),
    sortByRatio,
  );
}

export function formatAllocationPercent(percent: number): string {
  if (percent > 0 && percent < 0.5) {
    return '<1%';
  }
  return safePercent(percent, 0);
}

export const CLIENT_PORTAL_ALLOCATION_TAIL_MIN_PERCENT = 1;
export const CLIENT_PORTAL_ALLOCATION_OTHER_LABEL = 'Other';

/**
 * Folds the sub-1% tail into one "Other" slice. Drawn one by one, a run of slivers is narrower than the seam
 * between them and renders as a gap in the ring — which reads as a sweep that stopped short of closing. One
 * small class keeps its own name; it is a run of them that is the problem.
 */
export function foldAllocationTail(
  slices: readonly ClientPortalAllocationSlice[],
  minPercent = CLIENT_PORTAL_ALLOCATION_TAIL_MIN_PERCENT,
): readonly ClientPortalAllocationSlice[] {
  const head = slices.filter((slice) => slice.percent >= minPercent);
  const tail = slices.filter((slice) => slice.percent < minPercent);
  if (tail.length < 2) {
    return slices;
  }
  return [
    ...head,
    {
      name: CLIENT_PORTAL_ALLOCATION_OTHER_LABEL,
      value: tail.reduce((sum, slice) => sum + slice.value, 0),
      percent: tail.reduce((sum, slice) => sum + slice.percent, 0),
      color: colorAt(head.length),
    },
  ];
}
